import type {FocusTrap} from 'focus-trap'
import FocusTrapReact from 'focus-trap-react'
import {isEqual} from 'lodash'
import * as React from 'react'
import {findDOMNode} from 'react-dom'

import {filterOutChildNodes} from '@base/dom-utils/nodes'
import {mergeProps} from '@base/react-utils'

import {boundaryDecorator, childBoundariesSelector} from './Boundary'

let activeBoundaryCount = 0

export const isFocusBoundaryActive = () => activeBoundaryCount > 0

const fakeFocusableNode = {
  focus: () => {},
  tagName: 'stub',
} as unknown as HTMLElement

const fakeFocusableNodeWithWarning = {
  focus: () => {
    // eslint-disable-next-line no-console
    console.warn('FocusBoundary didnt have anything to set focus on')
  },
  tagName: 'stub',
} as unknown as HTMLElement

type FocusBoundaryProps = {
  active?: boolean
  children: React.ReactElement<any>
  returnFocus?: (
    lastInFocus: HTMLElement | undefined
  ) => HTMLElement | undefined
  // Automatically find element with lowest tabIndex and focus on it
  // Otherwise, an element with `autoFocus` prop will be focused, or the boundary container if not.
  // It's false by default
  autoFocus?: boolean
}

// FocusBoundary adds support for Portals to FocusTrap by updating the list of tabbable containers
// whenever component is rendered or user navigates the page.
// It's important, as Portaled content is in a different DOM tree and won't be accessible once trap
// is activated. As portaled popovers may be opened/closed at any point in time, we need to
// update the containers list pretty often.

// @ts-expect-error Class decorators don't play well with TS
@boundaryDecorator({boundaryName: 'FocusBoundary'})
class FocusBoundary extends React.Component<FocusBoundaryProps> {
  static displayName = 'FocusBoundary'

  static defaultProps = {
    active: true,
    autoFocus: false,
    returnFocus: undefined,
  }

  // @ts-expect-error no initializer as it comes from @boundaryDecorator
  boundaries: Array<string>
  // @ts-expect-error no initializer as it comes from @boundaryDecorator
  boundaryProps: Object
  containers: Array<HTMLElement> = []
  focusTrapRef: {
    focusTrap: FocusTrap
    updatePreviousElement: () => void
  } | null = null

  constructor(props) {
    super(props)
    // We need to update container BEFORE FocusTrap handles the focus itself
    const capture = {
      capture: true,
      passive: false,
    }
    document.addEventListener('focusin', this.updateContainers, capture)
    document.addEventListener('mousedown', this.updateContainers, capture)
    document.addEventListener('touchstart', this.updateContainers, capture)
    document.addEventListener('click', this.updateContainers, capture)
    document.addEventListener('keydown', this.updateContainers, capture)
  }

  componentWillUnmount() {
    document.removeEventListener('focusin', this.updateContainers, true)
    document.removeEventListener('mousedown', this.updateContainers, true)
    document.removeEventListener('touchstart', this.updateContainers, true)
    document.removeEventListener('click', this.updateContainers, true)
    document.removeEventListener('keydown', this.updateContainers, true)
  }

  componentDidMount() {
    this.updateContainers()
  }

  componentDidUpdate(prevProps) {
    if (this.focusTrapRef) {
      if (
        !prevProps.active &&
        this.props.active &&
        !this.focusTrapRef.focusTrap
      ) {
        // Focus Trap React stores previous element only when FocusTrap is first mounted,
        // not when it's activated for the first time.
        this.focusTrapRef.updatePreviousElement()
      }
    }
    this.updateContainers()
  }

  containsNode(node) {
    return this.containers.some((container) => container?.contains(node))
  }

  /** Single focus boundary may be spanned across multiple DOM elements with the help of portals
   *
   * getContainers() returns all top-level containers. These will be used to gather tabbable children from them by focus-trap
   * */
  getContainers(): HTMLElement[] {
    const containers: HTMLElement[] = [
      findDOMNode(this) as HTMLElement,
      ...(Array.from(
        document.querySelectorAll(childBoundariesSelector(this.boundaries))
      ) as HTMLElement[]),
    ].filter(Boolean)
    return filterOutChildNodes(containers)
  }

  updateContainers = () => {
    if (this.props.active) {
      const newContainers = this.getContainers()
      if (!isEqual(this.containers, newContainers)) {
        this.containers = newContainers
        const focusTrap = this.focusTrapRef?.focusTrap
        if (focusTrap) {
          focusTrap.updateContainerElements(newContainers)
        } else {
          // if focusTrap wasn't created yet, we need to re-render
          // FocusTrapReact will setup the trap only once it gets at least one container
          this.forceUpdate()
        }
      }
    }
  }

  getInitialFocus = () => {
    // If there's already an auto-focused element, don't change it...
    if (this.containsNode(document.activeElement)) {
      return document.activeElement
    }
    // Fallback to the boundary itself
    return findDOMNode(this) || fakeFocusableNode
  }

  setFocusTrapRef = (ref) => {
    this.focusTrapRef = ref
  }

  render() {
    const {children, active, autoFocus, returnFocus, ...otherProps} = this.props

    return (
      // We don't want FocusTrap to pass `ref` to the child component, as most our use-cases won't handle refs
      // The only way to force it, is to not pass any children inside it
      <>
        <FocusTrapReact
          active={active}
          focusTrapOptions={{
            onActivate() {
              activeBoundaryCount += 1
            },
            onDeactivate() {
              activeBoundaryCount -= 1
            },
            // @ts-expect-error Lib's typing is wrong here
            initialFocus: autoFocus ? undefined : this.getInitialFocus,
            // FocusTrap may throw if there's nothing to focus. It may happen if multiple FocusTraps are
            // unmounted at the same time. So it's safer to just give it a fake focusable than to throw...
            fallbackFocus: () => fakeFocusableNodeWithWarning,
            clickOutsideDeactivates: false,
            escapeDeactivates: false,
            allowOutsideClick: true,
            preventScroll: false,
            returnFocusOnDeactivate: !!returnFocus,
            // We have a patch in focus-trap-react that gives the last focus node to the callback
            setReturnFocus: returnFocus as any,
          }}
          containerElements={this.containers}
          ref={this.setFocusTrapRef}
        />
        {React.cloneElement(
          children,
          mergeProps(
            {tabIndex: -1},
            children.props,
            otherProps,
            this.boundaryProps
          )
        )}
      </>
    )
  }
}

export default FocusBoundary
